import React, { lazy, Suspense, useMemo } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ToasterProvider } from '@sgwt/react-shared-libs';
import { SgwtHelpCenter, SgwtWebAnalyticsHTMLElement } from '@sgwt/sgwt-widgets-react';
import { SgErrorPage } from '@sg-bootstrap/components/dist/react-library/src/components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { QueryParamProvider } from 'use-query-params';
import { AdminProvider } from '../../contexts/AdminContext';
import { ApplicationProvider, useApplicationState } from '../../contexts/ApplicationContext';
import { NotificationContextProvider } from '../../contexts/NotificationContext';
import { RequestPriorityProvider } from '../../contexts/RequestPriorityContext';
import { SessionContextProvider, useSession } from '../../contexts/SessionContext';
import { UIProvider } from '../../contexts/UIContext';
import { UrlProvider } from '../../contexts/UrlContext';
import { useApplicationsCountMine } from '../../hooks/useApplicationsCountMine';
import { useStatus } from '../../hooks/useStatus';
import { UserPermissions } from '../../types';
import { getHomePath } from '../../utils/personalization.util';
import { ErrorBoundary, Header, MiniFooter } from '../common';
import { Maintenance } from '../shared/banners/Maintenance';
import { Loader } from '../shared/common/Loader';
import { ProtectedRoute } from '../shared/common/ProtectedRoute';
import { GlobalStateProvider, useGlobalState } from '../../contexts/GlobalStateContext';
import { chatbotConfiguration, getSgConnectUserInfo } from '../../helpers';

import './App.scss';

const WelcomeModal = lazy(() => import('../shared/modals/WelcomeModal'));
const Home = lazy(() => import('../routes/Home/Home'));
const ProfilePage = lazy(() => import('../routes/Profile/ProfilePage'));
const ReportingPage = lazy(() => import('../routes/Reporting/ReportingPage'));
const Certifications = lazy(() => import('../routes/Certifications/Certifications'));
const AdministrationPage = lazy(() => import('../routes/Administration/AdministrationPage'));
const PageNotFound = lazy(() => import('../common/PageNotFound'));
const PageLoading = lazy(() => import('../common/PageLoading'));

/**
 * Reimplementation of the ReactRouter6Adapter that doesn't remove location hash
 * Basic implementation remove the location hash
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ReactRouter6Adapter = ({ children }: { children: any }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const adapter = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    replace(location2: any) {
      navigate(location2.search + location2.hash || '?', {
        replace: true,
        state: location2.state,
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    push(location2: any) {
      navigate(location2.search + location2.hash || '?', {
        replace: false,
        state: location2.state,
      });
    },
    get location() {
      let _a;
      const nav = (_a = navigator == null ? void 0 : (navigator as any).location);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return nav != null ? _a : location;
    },
  };
  return children(adapter);
};

const AccessController: React.FC = () => {
  const { pathname } = useLocation();
  const { userFull, profileType, setProfileType } = useSession();
  const { userApplicationsCount } = useApplicationState();
  const { loadingFilters } = useGlobalState();

  useApplicationsCountMine({ shouldFetch: userApplicationsCount === undefined });

  const { globalStatus } = useStatus({
    defaultValue: 'idle',
    deps: [pathname],
  });

  // sgwt-web-analytics
  const webAnalyticsWidget = document.querySelector<SgwtWebAnalyticsHTMLElement>('sgwt-web-analytics');
  if (webAnalyticsWidget) {
    webAnalyticsWidget.addEventListener('sgwt-web-analytics--ready', webAnalyticsReady);
  }

  function webAnalyticsReady() {
    if (!webAnalyticsWidget || !userFull) {
      return;
    }

    webAnalyticsWidget.setCustomDimension(1, userFull.rc_local_sigle);
  }

  React.useLayoutEffect(() => {
    if (userFull && userFull.profileType) {
      setProfileType(userFull.profileType);
    }

    // eslint-disable-next-line
  }, [userFull]);

  const personalizePath = getHomePath(profileType);

  return (
    <>
      {userFull && profileType === null && <WelcomeModal />}
      <Routes>
        {['/', '/explore'].map((path) => (
          <Route key={path} path={path} element={<Navigate to={personalizePath} />} />
        ))}
        <Route
          path={'/explore/*'}
          element={
            <>
              {typeof globalStatus === 'undefined' ? (
                <div className="mx-5 pt-5 d-flex flex-column justif-content-between flex-grow-1">
                  <Loader />
                </div>
              ) : globalStatus === 'maintenance' ? (
                <Maintenance />
              ) : (
                <UrlProvider>
                  <Home loadingFilters={loadingFilters} />
                </UrlProvider>
              )}
            </>
          }
        />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/reporting/*" element={<ReportingPage />} />
        <Route
          path="/certifications"
          element={
            <ProtectedRoute
              requiredPermissions={[UserPermissions.ADMIN, UserPermissions.MODERATOR]}
              exceptions={userApplicationsCount !== undefined && userApplicationsCount > 1}>
              <Certifications />
            </ProtectedRoute>
          }
        />

        <Route
          path="/administration/*"
          element={
            <ProtectedRoute
              requiredPermissions={[
                UserPermissions.ADMIN,
                UserPermissions.MODERATOR,
                UserPermissions.AUDITOR,
                UserPermissions.REVIEWER,
                UserPermissions.ARCHITECT,
              ]}>
              <AdminProvider>
                <AdministrationPage />
              </AdminProvider>
            </ProtectedRoute>
          }
        />

        {typeof userFull !== 'undefined' &&
          typeof userApplicationsCount !== 'undefined' &&
          profileType !== undefined && <Route path="*" element={<PageNotFound />} />}
      </Routes>
    </>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  const sgConnectUserInfo = useMemo(() => getSgConnectUserInfo(), [window.SGWTWidgetConfiguration]);

  if (!sgConnectUserInfo) {
    return <PageLoading />;
  }
  if (sgConnectUserInfo.origin_network !== 'LAN') {
    return (
      <SgErrorPage code={403} titleLabel="Forbidden" descriptionLabel="Only LAN user can access Platformization." />
    );
  }

  return (
    <ErrorBoundary>
      <SessionContextProvider>
        <NotificationContextProvider>
          <Router>
            <ToasterProvider>
              <QueryParamProvider adapter={ReactRouter6Adapter}>
                <RequestPriorityProvider>
                  <GlobalStateProvider>
                    <UIProvider>
                      <ApplicationProvider>
                        <QueryClientProvider client={queryClient}>
                          <Header />
                          <div className="d-flex flex-column main-content bg-lvl2">
                            <Suspense
                              fallback={
                                <div className="d-flex flex-column">
                                  <Loader withoutText={false} className="mx-5 mt-5" />
                                </div>
                              }>
                              <AccessController />
                            </Suspense>
                          </div>
                          <SgwtHelpCenter
                            applicationId="modernization"
                            chatbotConfiguration={chatbotConfiguration}
                            contactUsUseChatbot
                          />
                          <MiniFooter />
                        </QueryClientProvider>
                      </ApplicationProvider>
                    </UIProvider>
                  </GlobalStateProvider>
                </RequestPriorityProvider>
              </QueryParamProvider>
            </ToasterProvider>
          </Router>
        </NotificationContextProvider>
      </SessionContextProvider>
    </ErrorBoundary>
  );
};

export default App;
